import { useCallback, useEffect, useState, CSSProperties } from "react";
import { Navigate } from "react-router-dom";
import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";

type AuthStatus = "checking" | "authorized" | "denied";

interface TokenInfo {
  idToken: string;
  accessToken: string;
  idPayload: Record<string, unknown> | null;
  accessPayload: Record<string, unknown> | null;
  expiresAt: number | null;
}

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

const styles: Record<string, CSSProperties> = {
  page: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  container: {
    width: "100%",
    maxWidth: "60rem",
    background: "#252d6d",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    flexWrap: "wrap",
    gap: "12px",
  },
  title: {
    fontSize: "28px",
    fontWeight: 700,
    margin: 0,
    background: "linear-gradient(90deg, #60a5fa, #a78bfa, #f472b6)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  subtitle: {
    fontSize: "13px",
    color: "#94a3b8",
    marginTop: "4px",
  },
  infoBox: {
    background: "rgba(251, 191, 36, 0.08)",
    border: "1px solid rgba(251, 191, 36, 0.25)",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "24px",
    fontSize: "14px",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  badge: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  card: {
    background: "rgba(15, 23, 42, 0.6)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "14px",
    padding: "20px",
    marginBottom: "20px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "14px",
    flexWrap: "wrap",
    gap: "10px",
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
    color: "#f1f5f9",
  },
  tokenBox: {
    background: "#020617",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "10px",
    padding: "14px",
    fontSize: "12px",
    fontFamily: "'SF Mono', Monaco, Menlo, Consolas, monospace",
    color: "#94a3b8",
    wordBreak: "break-all",
    maxHeight: "120px",
    overflowY: "auto",
    lineHeight: 1.5,
  },
  payloadBox: {
    background: "#020617",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "10px",
    padding: "14px",
    fontSize: "12px",
    fontFamily: "'SF Mono', Monaco, Menlo, Consolas, monospace",
    color: "#cbd5e1",
    overflowX: "auto",
    marginTop: "10px",
    whiteSpace: "pre",
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "transform 0.15s, box-shadow 0.15s",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
  },
  btnCopy: {
    background: "rgba(34, 197, 94, 0.15)",
    color: "#4ade80",
    border: "1px solid rgba(34, 197, 94, 0.3)",
    padding: "8px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  btnCopied: {
    background: "rgba(34, 197, 94, 0.3)",
    color: "#86efac",
  },
  btnGhost: {
    background: "rgba(255, 255, 255, 0.05)",
    color: "#cbd5e1",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    padding: "8px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  centerBox: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    fontSize: "16px",
    background: "#0f172a",
  },
};

export default function TokenDebugPage() {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [userEmail, setUserEmail] = useState<string>("");
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  // const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [copiedKey, setCopiedKey] = useState<string>("");

  const loadTokens = useCallback(async (forceRefresh = false) => {
    // setLoading(true);
    setError("");
    try {
      const session = await fetchAuthSession({ forceRefresh });
      const idToken = session.tokens?.idToken?.toString() || "";
      const accessToken = session.tokens?.accessToken?.toString() || "";

      if (!idToken && !accessToken) {
        throw new Error("No hay sesión activa. Inicia sesión primero.");
      }

      const idPayload = idToken ? decodeJwt(idToken) : null;
      const accessPayload = accessToken ? decodeJwt(accessToken) : null;
      const exp = (idPayload?.exp as number | undefined) ?? null;

      setTokenInfo({
        idToken,
        accessToken,
        idPayload,
        accessPayload,
        expiresAt: exp ? exp * 1000 : null,
      });
      return true;
    } catch (e) {
      console.error("Error obteniendo tokens:", e);
      setError(e instanceof Error ? e.message : "Error desconocido");
      return false;
    } finally {
      // setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const session = await fetchAuthSession();
        const hasToken = !!session.tokens?.idToken || !!session.tokens?.accessToken;

        if (cancelled) return;

        if (!hasToken) {
          setStatus("denied");
          return;
        }

        // Intentar obtener email (no bloquea si falla)
        try {
          const attributes = await fetchUserAttributes();
          if (!cancelled) {
            const email = (attributes.email || "").toLowerCase().trim();
            setUserEmail(email || "usuario");
          }
        } catch {
          if (!cancelled) setUserEmail("usuario");
        }

        if (cancelled) return;
        setStatus("authorized");
        await loadTokens(false);
      } catch (e) {
        console.error("Error verificando sesión:", e);
        if (!cancelled) setStatus("denied");
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [loadTokens]);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1800);
    } catch (e) {
      console.error("Error copiando al portapapeles:", e);
    }
  };

  if (status === "checking") {
    return <div style={styles.centerBox}>Verificando acceso...</div>;
  }

  if (status === "denied") {
    return <Navigate to="/" replace />;
  }

  const expiresAtStr = tokenInfo?.expiresAt
    ? new Date(tokenInfo.expiresAt).toLocaleString()
    : "—";
  const isExpired = tokenInfo?.expiresAt
    ? Date.now() > tokenInfo.expiresAt
    : false;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>🔐 Token Debug</h1>
            <div style={styles.subtitle}>Easter Egg · Solo para tests</div>
          </div>
          {/* <button
            style={{ ...styles.btnPrimary, opacity: loading ? 0.6 : 1 }}
            onClick={() => loadTokens(true)}
            disabled={loading}
          >
            {loading ? "⏳ Cargando..." : "🔄 Refrescar token"}
          </button> */}
        </div>

        <div style={styles.infoBox}>
          <div style={styles.infoRow}>
            <span>
              <strong style={{ color: "#fbbf24" }}>Email:</strong>{" "}
              <span style={{ color: "#fde68a" }}>{userEmail}</span>
            </span>
            <span
              style={{
                ...styles.badge,
                background: isExpired
                  ? "rgba(239, 68, 68, 0.2)"
                  : "rgba(34, 197, 94, 0.2)",
                color: isExpired ? "#fca5a5" : "#86efac",
                border: `1px solid ${
                  isExpired
                    ? "rgba(239, 68, 68, 0.4)"
                    : "rgba(34, 197, 94, 0.4)"
                }`,
              }}
            >
              {isExpired ? "Expirado" : "Válido"}
            </span>
          </div>
          <div style={{ marginTop: "8px", fontSize: "13px", color: "#cbd5e1" }}>
            <strong style={{ color: "#fbbf24" }}>Expira:</strong> {expiresAtStr}
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              color: "#fca5a5",
              padding: "12px 16px",
              borderRadius: "10px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {tokenInfo && (
          <>
            <TokenSection
              title="ID Token"
              token={tokenInfo.idToken}
              payload={tokenInfo.idPayload}
              copyKey="id"
              copiedKey={copiedKey}
              onCopy={copyToClipboard}
            />
            {/* <TokenSection
              title="Access Token"
              token={tokenInfo.accessToken}
              payload={tokenInfo.accessPayload}
              copyKey="access"
              copiedKey={copiedKey}
              onCopy={copyToClipboard}
            /> */}
          </>
        )}

        <div
          style={{
            textAlign: "center",
            color: "#64748b",
            fontSize: "12px",
            marginTop: "30px",
            paddingTop: "20px",
            borderTop: "1px solid rgba(255, 255, 255, 0.05)",
          }}
        >
          ⚠️ No compartas estos tokens. Son sensibles.
        </div>
      </div>
    </div>
  );
}

interface TokenSectionProps {
  title: string;
  token: string;
  payload: Record<string, unknown> | null;
  copyKey: string;
  copiedKey: string;
  onCopy: (text: string, key: string) => void;
}

function TokenSection({
  title,
  token,
  // payload,
  copyKey,
  copiedKey,
  onCopy,
}: TokenSectionProps) {
  // const [showPayload, setShowPayload] = useState(true);
  const isCopied = copiedKey === copyKey;

  if (!token) {
    return (
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>{title}</h2>
        <p style={{ color: "#64748b", marginTop: "8px", fontSize: "14px" }}>
          No disponible
        </p>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <h2 style={styles.cardTitle}>{title}</h2>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            style={{
              ...styles.btnCopy,
              ...(isCopied ? styles.btnCopied : {}),
            }}
            onClick={() => onCopy(token, copyKey)}
          >
            {isCopied ? "✓ Copiado" : "📋 Copiar token"}
          </button>
          {/* {payload && (
            <button
              style={styles.btnGhost}
              onClick={() => setShowPayload((v) => !v)}
            >
              {showPayload ? "Ocultar payload" : "Ver payload"}
            </button>
          )} */}
        </div>
      </div>

      <div style={styles.tokenBox}>{token}</div>

      {/* {showPayload && payload && (
        <pre style={styles.payloadBox}>
          {JSON.stringify(payload, null, 2)}
        </pre>
      )} */}
    </div>
  );
}
